import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CursorPaginatedResponse,
  PackageDeal,
  PackageDealCreatePayload,
  PackageDealData,
  PackageDealListParams,
  PackageDealUpdatePayload,
} from '@/libs/interfaces'
import { packageDealsApiMock } from '@/libs/mocks/handlers/packageDeals'

const packageDealsApiReal = {
  listCursor: async (
    params: PackageDealListParams,
  ): Promise<CursorPaginatedResponse<PackageDeal>> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<PackageDealData>>(URLs.PackageDeals.list(orgID, storeID, query))
      .then((res) => res?.data?.data)
    return {
      data: response.packageDeals ?? [],
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },

  list: async (params: PackageDealListParams): Promise<PackageDeal[]> => {
    const { orgID, storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<PackageDealData>>(URLs.PackageDeals.list(orgID, storeID, query))
      .then((res) => res?.data?.data?.packageDeals ?? [])
  },

  getByID: async (params: {
    orgID: string
    storeID: string
    packageID: string
  }): Promise<Partial<PackageDeal>> => {
    const { orgID, storeID, packageID } = params
    return await api
      .get<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.byID(orgID, storeID, packageID))
      .then((res) => res.data.data)
  },

  create: async (
    body: PackageDealCreatePayload & { orgID: string; storeID: string },
  ): Promise<CreateApiResult<Partial<PackageDeal>>> => {
    const { orgID, storeID, ...payload } = body
    return await api
      .post<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.create(orgID, storeID), payload)
      .then((res) => ({
        data: res.data.data,
        status: res.status,
        message: res.message,
      }))
  },

  update: async (
    payload: PackageDealUpdatePayload & { orgID: string; storeID: string; packageID: string },
  ): Promise<Partial<PackageDeal>> => {
    const { orgID, storeID, packageID, ...patchData } = payload
    return await api
      .patch<ApiResponse<Partial<PackageDeal>>>(
        URLs.PackageDeals.byID(orgID, storeID, packageID),
        patchData,
      )
      .then((res) => res.data.data)
  },

  delete: async (payload: Partial<PackageDeal>): Promise<Partial<PackageDeal>> => {
    const { orgID = '', storeID = '', packageID = '' } = payload
    return await api
      .delete<ApiResponse<Partial<PackageDeal>>>(URLs.PackageDeals.byID(orgID, storeID, packageID))
      .then((res) => res.data?.data)
  },
}

export const packageDealsApi: typeof packageDealsApiReal = __IS_MOCK_MODE__
  ? packageDealsApiMock
  : packageDealsApiReal
